import {
  type ActionFunctionArgs,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "react-router";
import {
  Form,
  useLoaderData,
  useActionData,
  useNavigation,
  Link,
} from "react-router";
import { getSession, commitSession } from "~/session.server";
import { requestPasswordReset } from "~/utils/ms-auth.server";
import {
  verifyTurnstileWithCache,
  getTurnstileSiteKey,
  isTurnstileChallengeRequired,
  getClientIP,
} from "~/utils/turnstile.server";
import { audit } from "~/utils/logger.server";
import { TurnstileWidget } from "~/components/auth/TurnstileWidget";
import { sendPasswordResetEmail } from "~/utils/email-templates.server";
import { APP_URL } from "~/utils/email.server";
import { Button } from "~/components/button/Button";
import { Alert } from "~/components/alert/Alert";
import { FormInput } from "~/components/form-input/FormInput";

export const meta: MetaFunction = () => [
  { title: "MediaScribe - Forgot Password" },
];

interface LoaderData {
  turnstileSiteKey: string | undefined;
  turnstileRequired: boolean;
}

export const loader = async ({
  request,
}: LoaderFunctionArgs): Promise<LoaderData> => {
  // Get session to check Turnstile cache
  const session = await getSession(request.headers.get("cookie"));

  // Check if Turnstile challenge is required (uses 25 min threshold)
  const turnstileRequired = isTurnstileChallengeRequired(session, request);

  return {
    turnstileSiteKey: getTurnstileSiteKey(),
    turnstileRequired,
  };
};

interface ActionData {
  error?: string;
  success?: boolean;
}

export const action = async ({
  request,
}: ActionFunctionArgs): Promise<ActionData | Response> => {
  const formData = await request.formData();
  const email = formData.get("email") as string;

  // Get session for Turnstile caching
  const session = await getSession(request.headers.get("cookie"));

  // Validate email
  if (!email || !email.includes("@")) {
    return { error: "Please enter a valid email address." };
  }

  // Verify Turnstile (uses session cache if available)
  const turnstileResult = await verifyTurnstileWithCache(
    formData,
    request,
    session,
  );
  if (!turnstileResult.success) {
    return { error: turnstileResult.error || "Bot verification failed." };
  }

  // Request password reset (this always returns success to prevent email enumeration)
  const result = await requestPasswordReset(email.trim().toLowerCase());

  // Audit the request regardless of whether the account exists (anti-enumeration
  // response is unchanged); `matched` reflects whether a reset token was issued.
  audit("password_reset.requested", {
    actor: email.trim().toLowerCase(),
    ip: getClientIP(request),
    outcome: "success",
    matched: Boolean(result.token),
  });

  // If user exists and token was generated, send the email
  if (result.token && result.userName) {
    const resetUrl = `${APP_URL}/auth/reset-password?token=${encodeURIComponent(result.token)}`;
    await sendPasswordResetEmail(email, resetUrl, result.userName);
  }

  // If Turnstile was freshly verified, commit session to save the cache
  if (turnstileResult.sessionUpdated) {
    return Response.json(
      { success: true },
      {
        headers: {
          "Set-Cookie": await commitSession(session),
        },
      },
    );
  }

  // Always return success to prevent email enumeration
  return { success: true };
};

export default function ForgotPassword() {
  const loaderData = useLoaderData<typeof loader>();
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";

  const { turnstileSiteKey, turnstileRequired } = loaderData;

  // Show success message
  if (actionData?.success) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-ms-gray-50 px-4">
        <div className="w-full max-w-md rounded-2xl border border-ms-gray-200 bg-white p-8 shadow-lg">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="mb-2 font-serif text-2xl text-ms-gray-900">
              Check your email
            </h1>
            <p className="mb-6 text-sm text-ms-gray-600">
              If an account exists with that email, {`we've`} sent a password
              reset link. The link will expire in 1 hour.
            </p>
            <Link
              to="/auth/login"
              className="rounded text-sm font-medium text-ms-primary-600 hover:text-ms-primary-500 focus:outline-none focus:ring-2 focus:ring-ms-primary-500 focus:ring-offset-2"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-ms-gray-50 px-4 py-12">
      <div className="w-full max-w-md rounded-2xl border border-ms-gray-200 bg-white p-8 shadow-lg">
        {/* Header */}
        <div className="mb-8 text-center">
          <img
            src="/images/mediascribe-logo.svg"
            alt="MediaScribe"
            className="mx-auto mb-6 h-10 w-auto"
          />
          <h1
            id="forgot-password-heading"
            className="font-serif text-2xl text-ms-gray-900"
          >
            Reset your password
          </h1>
          <p className="mt-2 text-sm text-ms-gray-600">
            Enter your email address and {`we'll`} send you a link to reset your
            password.
          </p>
        </div>

        {/* Error message */}
        {actionData?.error && (
          <Alert variant="error" className="mb-6">
            {actionData.error}
          </Alert>
        )}

        <Form
          method="post"
          aria-labelledby="forgot-password-heading"
          className="space-y-5"
        >
          {/* Email */}
          <FormInput
            type="email"
            name="email"
            label="Email"
            required
            autoComplete="email"
            placeholder="you@example.com"
          />

          {/* Turnstile */}
          <TurnstileWidget
            siteKey={turnstileSiteKey || ""}
            className="mt-4"
            resetKey={actionData?.error}
            skip={!turnstileRequired}
          />

          {/* Submit */}
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            loadingText="Sending..."
            className="w-full"
            size="md"
          >
            Send reset link
          </Button>
        </Form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <Link
            to="/auth/login"
            className="rounded text-sm font-medium text-ms-primary-600 hover:text-ms-primary-500 focus:outline-none focus:ring-2 focus:ring-ms-primary-500 focus:ring-offset-2"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </main>
  );
}
